/**
 * RevealScreen component
 *
 * Shows the image details, AI status, stats, and contradiction callouts
 */

import { useTranslation } from 'react-i18next';
import { ExternalLink, ArrowRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { AiStatusBadge } from '../ui/Badge';
import { StatsDisplay } from './StatsDisplay';
import { ContradictionCallout } from './ContradictionCallout';
import type { RevealedImage } from '../../hooks/useVoteFlow';
import type { Contradiction } from '@doyoulikethis/shared';

interface RevealScreenProps {
  image: RevealedImage;
  userVote: boolean;
  userReasons: string[];
  contradiction: Contradiction | null;
  onNext: () => void;
}

export function RevealScreen({
  image,
  userVote,
  userReasons,
  contradiction,
  onNext,
}: RevealScreenProps) {
  const { t, i18n } = useTranslation();
  const isJapanese = i18n.language === 'ja';

  const title = isJapanese && image.title_ja ? image.title_ja : image.title_en;

  return (
    <div className="mx-auto w-full max-w-4xl animate-fade-in">
      <div className="grid gap-6 md:grid-cols-2">
        {/* Image */}
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-600 dark:bg-black">
          <div className="relative aspect-[4/3] w-full bg-gray-100 dark:bg-gray-900">
            <img
              src={image.cdn_url}
              alt={title}
              className="absolute inset-0 h-full w-full object-contain"
            />
          </div>
        </div>

        {/* Details */}
        <div className="flex flex-col">
          {/* Title and artist */}
          <div className="mb-4">
            <h2 className="text-2xl font-bold">{title}</h2>
            {image.artist && (
              <p className="mt-1 text-gray-600 dark:text-gray-200">
                By: {image.artist}
              </p>
            )}
          </div>

          {/* Source link */}
          <a
            href={image.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mb-4 inline-flex items-center gap-1 text-sm text-accent hover:underline"
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            {t('reveal.view_source')}
          </a>

          {/* AI Status badge */}
          <div className="mb-4">
            <AiStatusBadge status={image.ai_status} />
            {image.ai_notes && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-200">
                {image.ai_notes}
              </p>
            )}
          </div>

          {/* Contradiction callout */}
          {contradiction && (
            <div className="mb-4">
              <ContradictionCallout
                type={contradiction.type}
                message={contradiction.message}
              />
            </div>
          )}

          {/* User's vote summary */}
          <div className="mb-4 rounded-lg bg-gray-100 p-3 dark:bg-gray-900">
            <p className="text-sm">
              You{' '}
              <span
                className={
                  userVote ? 'font-semibold text-vote-yes' : 'font-semibold text-vote-no'
                }
              >
                {userVote ? 'liked' : 'disliked'}
              </span>{' '}
              this image
              {userReasons.length > 0 && (
                <span className="text-gray-600 dark:text-gray-200">
                  {' '}
                  because it{' '}
                  {userReasons
                    .map((r) => t(`reasons.${userVote ? 'yes' : 'no'}.${r}`))
                    .join(', ')
                    .toLowerCase()}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="mt-6">
        <StatsDisplay
          stats={image.stats}
          aiStatus={image.ai_status}
        />
      </div>

      {/* Next button */}
      <div className="mt-8 flex justify-center">
        <Button
          variant="primary"
          size="lg"
          onClick={onNext}
          className="group"
        >
          {t('reveal.next_image')}
          <ArrowRight
            className="h-5 w-5 transition-transform group-hover:translate-x-1"
            aria-hidden="true"
          />
        </Button>
      </div>
    </div>
  );
}
